import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { ErrorMessage } from 'app/core/error-handler/constants';
import { Error } from 'app/core/error-handler/models';
import { createErrorRaised } from 'app/core/error-handler/state/actions';
import { LoadDetailedUsers } from 'app/core/user/state/actions';
import { LoadAssessmentActivityData } from 'app/reports/activity-reports/state/actions/activity-report-load.action';
import { LoadAssessmentResults } from 'app/reports/assessment-results/state/assessment-results-load.action';
import {
  LoadDiscomfortLevelTrend,
  LoadEmployeeDiscomfortLevel,
  LoadEmployeeRiskLevel,
  LoadPosturalRisk,
  LoadProgramBenefits,
  LoadRiskLevelTrend,
  LoadTopTenDiscomfort,
} from 'app/reports/management-overview/state/actions';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { FilterInterface, FilterItemInterface, SiteFilterInterface } from '../models';
import { SiteFilterRestConnectorService } from '../services/site-filter-rest-connector.service';

import { SiteFilterActionType } from './actions';
import { CreateManagementSiteFilterItem, CreateManagementSiteFilterItemCompleted } from './actions/site-filter-create.action';
import { DeleteManagementSiteFilterItem, DeleteManagementSiteFilterItemCompleted } from './actions/site-filter-delete.action';
import { LoadManagmentSiteFilterCompleted, LoadSiteFilter, LoadSiteFilterCompleted } from './actions/site-filter-load.action';
import {
  UpdateManagementSiteFilterItem,
  UpdateManagementSiteFilterItemCompleted,
  UpdateManagmentSiteFilterCategory,
  UpdateManagmentSiteFilterCategoryCompleted,
  UpdateSiteFilter,
  UpdateSiteFilterCompleted } from './actions/site-filter-update.action';

@Injectable()
export class SiteFilterEffects {
  public source: string = 'SiteFilterEffects';

  // Load the site filter
  @Effect()
  public loadSiteFilter$: Observable<Action> = this._action$
    .pipe(
      ofType(SiteFilterActionType.LoadSiteFilter),
      switchMap(() => {
        return this._siteFilterRestConnectorService.loadFilter().pipe(
            map((siteFilter: SiteFilterInterface) => new LoadSiteFilterCompleted(siteFilter)),
            catchError((err: any) => of(createErrorRaised(new Error({
              message: ErrorMessage.unexpected,
              errorResponse: err,
              source: this.source,
            })))),
          );
      }),
    );

  // Update the site filter
  @Effect()
  public updateSiteFilter$: Observable<Action> = this._action$
  .pipe(
    ofType(SiteFilterActionType.UpdateSiteFilter),
    map((action: UpdateSiteFilter) => action.payload),
    switchMap((siteFilter: SiteFilterInterface) => {
      return this._siteFilterRestConnectorService.updateFilter(siteFilter).pipe(
        switchMap((updatedFilter: SiteFilterInterface) => [
          new UpdateSiteFilterCompleted(updatedFilter),
          new LoadTopTenDiscomfort(),
          new LoadDiscomfortLevelTrend(),
          new LoadRiskLevelTrend(),
          new LoadPosturalRisk(),
          new LoadProgramBenefits(),
          new LoadEmployeeDiscomfortLevel(),
          new LoadEmployeeRiskLevel(),
          new LoadDetailedUsers(),
          new LoadAssessmentActivityData(),
          new LoadAssessmentResults(),
        ]),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }));

  // Site filter management effects //

  @Effect()
  public loadManagementFilters$: Observable<Action> = this._action$
  .pipe(
    ofType(SiteFilterActionType.LoadManagmentSiteFilter),
    switchMap(() => {
      return this._siteFilterRestConnectorService.getManagementFilters().pipe(
        map((filterData: FilterInterface[]) => new LoadManagmentSiteFilterCompleted(filterData)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }));

  @Effect()
  public updateManagementFilterCategory$: Observable<Action> = this._action$
  .pipe(
    ofType(SiteFilterActionType.UpdateManagmentSiteFilterCategory),
    map((action: UpdateManagmentSiteFilterCategory) => action.payload),
    switchMap((filterToUpdate: FilterInterface) => {
      return this._siteFilterRestConnectorService.updateManagmentFilterCategory(filterToUpdate).pipe(
        switchMap((updatedData: FilterInterface) => [
          new UpdateManagmentSiteFilterCategoryCompleted(updatedData),
          new LoadSiteFilter()]),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }));

  @Effect()
  public createManagementFilterItem$: Observable<Action> = this._action$
  .pipe(
    ofType(SiteFilterActionType.CreateManagementSiteFilterItem),
    map((action: CreateManagementSiteFilterItem) => action.payload),
    switchMap((itemData: { filterId: number, filterItem: FilterItemInterface }) => {
      return this._siteFilterRestConnectorService.createManagementFilterItem(itemData.filterId, itemData.filterItem).pipe(
        switchMap((createdData: { filterId: number, filterItem: FilterItemInterface }) => [
          new CreateManagementSiteFilterItemCompleted(createdData),
          new LoadSiteFilter()]),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }));

  @Effect()
  public updateManagementFilterItem$: Observable<Action> = this._action$
  .pipe(
    ofType(SiteFilterActionType.UpdateManagementSiteFilterItem),
    map((action: UpdateManagementSiteFilterItem) => action.payload),
    switchMap((itemData: { filterId: number, filterItem: FilterItemInterface }) => {
      return this._siteFilterRestConnectorService.updateManagementFilterItem(itemData.filterId, itemData.filterItem).pipe(
        switchMap((updatedData: { filterId: number, filterItem: FilterItemInterface }) => [
          new UpdateManagementSiteFilterItemCompleted(updatedData),
          new LoadSiteFilter()]),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }));

  @Effect()
  public deleteManagementFilterItem$: Observable<Action> = this._action$
  .pipe(
    ofType(SiteFilterActionType.DeleteManagementSiteFilterItem),
    map((action: DeleteManagementSiteFilterItem) => action.payload),
    switchMap((itemData: { filterId: number, filterItem: FilterItemInterface }) => {
      return this._siteFilterRestConnectorService.deleteManagementFilterItem(itemData.filterId, itemData.filterItem).pipe(
        switchMap((updatedData: { filterId: number, filterItem: FilterItemInterface }) => [
          new DeleteManagementSiteFilterItemCompleted(updatedData),
          new LoadSiteFilter()]),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }));

  constructor(private readonly _action$: Actions, private readonly _siteFilterRestConnectorService: SiteFilterRestConnectorService) {}
}
